// DataSiswa.jsx
import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiDownload } from "react-icons/fi";
import AdminLayout from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext"; // ✅
import FormDataSiswa from "./FormDataSiswa";
import ConfirmDeleteModal from "../../components/modal/Delete";

export default function DataSiswa() {
  const { token } = useAuth(); // ✅

  const [siswa, setSiswa] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [search, setSearch] = useState("");
  const [filterKelas, setFilterKelas] = useState("");

  const fetchKelas = useCallback(async () => {
    try {
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/kelas", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = res.data.sort((a, b) => a.nama_kelas.localeCompare(b.nama_kelas));
      setKelasList(sorted);
      const kelas7A = sorted.find((k) => k.nama_kelas.toUpperCase() === "7A");
      if (kelas7A) setFilterKelas(kelas7A.id_kelas);
    } catch { toast.error("Gagal mengambil data kelas"); }
  }, [token]);

  const fetchSiswa = useCallback(async () => {
    try {
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/datasiswa", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSiswa(res.data);
    } catch { toast.error("Gagal mengambil data siswa"); }
  }, [token]);

  useEffect(() => { fetchKelas(); fetchSiswa(); }, [fetchKelas, fetchSiswa]);

  const viewQR = async (id) => {
    try {
      const res = await axios.get(`https://backend.smpn1kamal.sch.id/api/datasiswa/view/${id}`);
      setQrImage(res.data.qr);
    } catch { toast.error("Gagal memuat QR Code"); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`https://backend.smpn1kamal.sch.id/api/datasiswa/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Siswa berhasil dihapus");
      fetchSiswa();
    } catch { toast.error("Gagal menghapus siswa"); }
    setOpenDelete(false);
  };

  const filteredSiswa = siswa.filter((s) => {
    const keyword = search.toLowerCase();
    return (
      (!filterKelas || s.id_kelas === Number(filterKelas)) &&
      (
        s.nama_lengkap.toLowerCase().includes(keyword) ||
        s.nisn.toLowerCase().includes(keyword) ||
        s.no_hp_ortu?.includes(keyword) ||
        String(s.no_absen).includes(keyword)
      )
    );
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-blue-600">Data Siswa</h1>
            <p className="text-sm text-slate-500">SMPN 1 Kamal</p>
          </div>
          <div className="flex gap-3">
            {filterKelas && (
              <button
                onClick={() => window.open(`https://backend.smpn1kamal.sch.id/api/datasiswa/kartu/kelas/${filterKelas}`, "_blank")}
                className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-full hover:bg-green-700 shadow-sm transition-all active:scale-95"
              >
                <FiDownload /> Unduh
              </button>
            )}
            <button
              onClick={() => { setEditData(null); setOpenForm(true); }}
              className="flex items-center gap-2 bg-indigo-500 text-white px-6 py-2 rounded-full hover:bg-blue-700 shadow-sm transition-all active:scale-95"
            >
              <FiPlus /> Tambah
            </button>
          </div>
        </div>

        {/* CARD */}
        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex flex-row items-center justify-between gap-3 mb-6">
            <select value={filterKelas} onChange={(e) => setFilterKelas(e.target.value)} className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm">
              <option value="">Semua Kelas</option>
              {kelasList.map((k) => (<option key={k.id_kelas} value={k.id_kelas}>{k.nama_kelas}</option>))}
            </select>
            <input type="text" placeholder="Cari..." value={search} onChange={(e) => setSearch(e.target.value)} className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm w-40 md:w-72" />
          </div>

          {/* MOBILE */}
          <div className="md:hidden space-y-4">
            {filteredSiswa.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">Tidak ada data siswa</p>
            ) : (
              filteredSiswa.map((s, i) => (
                <div key={s.id} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-xs font-mono text-slate-400">#{i + 1}</p>
                    <span className="bg-blue-50 text-blue-600 px-3 py-1 text-xs font-bold rounded-full">{s.nama_kelas}</span>
                  </div>
                  <p className="font-bold text-slate-800 text-lg uppercase">{s.nama_lengkap}</p>
                  <p className="text-sm text-slate-500 mb-2">JK: {s.jenis_kelamin}</p>
                  <p className="text-sm text-slate-500 mb-4">NISN: {s.nisn}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-4">
                    <div className="bg-slate-50 p-2 rounded-lg"><p className="text-slate-400">No Absen</p><p className="font-bold">{s.no_absen}</p></div>
                    <div className="bg-slate-50 p-2 rounded-lg"><p className="text-slate-400">HP Ortu</p><p className="font-bold">{s.no_hp_ortu || "-"}</p></div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <button onClick={() => viewQR(s.id)} className="flex-1 flex items-center justify-center gap-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 py-2 rounded-full text-sm font-medium transition-colors"><FiEye /> Lihat QR</button>
                    <button onClick={() => window.open(`https://backend.smpn1kamal.sch.id/api/datasiswa/kartu/${s.id}`, "_blank")} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-full text-sm font-medium"><FiDownload /> Kartu</button>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditData(s); setOpenForm(true); }} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-full text-sm font-medium"><FiEdit2 /> Edit</button>
                    <button onClick={() => { setDeleteId(s.id); setOpenDelete(true); }} className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-2 rounded-full text-sm font-medium"><FiTrash2 /> Hapus</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* DESKTOP */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-4 text-left font-bold w-12">NO</th>
                  <th className="px-4 py-4 text-left font-bold w-32">NISN</th>
                  <th className="px-4 py-4 text-left font-bold">NAMA LENGKAP</th>
                  <th className="px-4 py-4 text-center font-bold w-20">L/P</th>
                  <th className="px-4 py-4 text-left font-bold w-48">NO WALI MURID</th>
                  <th className="px-4 py-4 text-center font-bold w-28">KARTU</th>
                  <th className="px-4 py-4 text-center font-bold w-24">AKSI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSiswa.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">Tidak ada data siswa</td></tr>
                ) : (
                  filteredSiswa.map((s, i) => (
                    <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-4 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-4 text-slate-600">{s.nisn}</td>
                      <td className="px-4 py-4 font-bold text-slate-800 uppercase">{s.nama_lengkap}</td>
                      <td className="px-4 py-4 text-center font-bold">{s.jenis_kelamin}</td>
                      <td className="px-4 py-4 text-slate-600">{s.no_hp_ortu || "-"}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-4">
                          <button onClick={() => viewQR(s.id)} className="text-indigo-500 hover:text-indigo-700 transition-colors" title="Lihat QR"><FiEye size={18} /></button>
                          <button onClick={() => window.open(`https://backend.smpn1kamal.sch.id/api/datasiswa/kartu/${s.id}`, "_blank")} className="text-green-500 hover:text-green-700 transition-colors" title="Download Kartu"><FiDownload size={18} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setEditData(s); setOpenForm(true); }} className="text-blue-500 hover:text-blue-700 transition-colors p-1"><FiEdit2 size={16} /></button>
                          <button onClick={() => { setDeleteId(s.id); setOpenDelete(true); }} className="text-red-500 hover:text-red-700 transition-colors p-1"><FiTrash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {openForm && <FormDataSiswa onClose={() => setOpenForm(false)} onSuccess={fetchSiswa} editData={editData} />}
        <ConfirmDeleteModal open={openDelete} onClose={() => setOpenDelete(false)} onConfirm={confirmDelete} title="Hapus Data Siswa" description="Apakah Anda yakin ingin menghapus data siswa ini?" />
      </div>

      {qrImage && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[999]" onClick={() => setQrImage(null)}>
          <div className="bg-white rounded-2xl p-5 flex flex-col items-center gap-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-slate-700">QR Code Siswa</p>
            <img src={qrImage} alt="QR" className="w-56" />
            <button onClick={() => setQrImage(null)} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium py-2 rounded-full transition">Tutup</button>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
}